import { useState, useCallback, useMemo } from 'react'
import type { Product } from '../data/products'

export interface CartItem {
  id: number
  name: string
  brand: string
  price: number
  image: string
  qty: number
}

export interface UseCartReturn {
  cart: CartItem[]
  addToCart: (product: Product) => void
  removeItem: (index: number) => void
  changeQty: (index: number, delta: number) => void
  totalItems: number
  totalPrice: number
  isOpen: boolean
  toggleCart: () => void
  openCart: () => void
  closeCart: () => void
  toastVisible: boolean
  showToast: () => void
}

export function useCart(): UseCartReturn {
  const [cart, setCart] = useState<CartItem[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [toastVisible, setToastVisible] = useState(false)

  const addToCart = useCallback((product: Product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === product.id)
      if (existing) {
        return prev.map((item) =>
          item.id === product.id ? { ...item, qty: item.qty + 1 } : item,
        )
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          brand: product.brand,
          price: product.price,
          image: product.image,
          qty: 1,
        },
      ]
    })
  }, [])

  const removeItem = useCallback((index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const changeQty = useCallback((index: number, delta: number) => {
    setCart((prev) => {
      const updated = [...prev]
      const newQty = updated[index].qty + delta
      if (newQty <= 0) {
        return updated.filter((_, i) => i !== index)
      }
      updated[index] = { ...updated[index], qty: newQty }
      return updated
    })
  }, [])

  const totalItems = useMemo(
    () => cart.reduce((sum, item) => sum + item.qty, 0),
    [cart],
  )

  const totalPrice = useMemo(
    () => cart.reduce((sum, item) => sum + item.price * item.qty, 0),
    [cart],
  )

  const toggleCart = useCallback(() => setIsOpen((prev) => !prev), [])
  const openCart = useCallback(() => setIsOpen(true), [])
  const closeCart = useCallback(() => setIsOpen(false), [])

  const showToast = useCallback(() => {
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 2500)
  }, [])

  return {
    cart,
    addToCart,
    removeItem,
    changeQty,
    totalItems,
    totalPrice,
    isOpen,
    toggleCart,
    openCart,
    closeCart,
    toastVisible,
    showToast,
  }
}
